import { DollarSign, HelpCircle, Eye, TrendingUp, Scale, Clock, FileCheck, Calculator, Handshake, Search, Calendar, Landmark, MapPin, Building2, Medal, BookOpen, Layers, Home, Users, Heart } from 'lucide-react';

export const sellerArticles = [
  {
    id: 'how-much-is-my-land-worth-for-development',
    category: 'Seller Guide',
    title: 'How Much Is My Land Worth for Development in Brisbane? 2026 Guide',
    excerpt: 'Understanding residual land value and what drives the price developers will pay for your property.',
    date: '2026-04-20',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800&q=85',
    icon: DollarSign,
    metaDescription: 'How much is your land worth as a development site? Learn about residual land value, what developers look for, and how to estimate the development potential of your property in Australia.',
    keyFacts: [
      'Development value is calculated using residual land value: GRV minus construction costs minus developer margin',
      'Properties zoned medium-density (MDR) or higher can be worth 30-100% more as development sites than as homes',
      'Key value drivers: zoning, lot size (800m2+), frontage width, slope gradient, and infrastructure proximity',
      'Brisbane inner-ring development sites (5km of CBD) typically sell between $1,500-$4,000 per sqm of land',
      'ACRES provides free development site assessments for property owners in South East Queensland — call 07 3096 0542',
    ],
    content: `
## Development Value vs Market Value

If you own a house on a large block in an area zoned for medium or high density, your property may be worth significantly more as a development site than as a home. The key is understanding the difference between **market value** (what a home buyer would pay) and **development value** (what a developer would pay).

Development value is calculated using a method called **residual land value**. It works backwards from the end product.

## How Residual Land Value Works

The formula is straightforward:

**Land Value = Gross Realisable Value − Development Costs − Developer's Margin**

For example, if a developer can build 10 apartments on your site, each selling for $600,000:

- **Gross Realisable Value (GRV)**: 10 × $600,000 = $6,000,000
- **Development costs** (construction, fees, finance, marketing): $4,000,000
- **Developer's margin** (20% of GRV): $1,200,000
- **Maximum land price**: $6,000,000 − $4,000,000 − $1,200,000 = **$800,000**

This is the maximum a developer can pay while still achieving an acceptable return. In practice, competitive tension between developers can push prices higher.

## Factors That Increase Your Land's Development Value

### Zoning and Height Limits
The single biggest factor. A property zoned for 6-storey apartments is worth dramatically more than one zoned for 2-storey townhouses. Check your local planning scheme for:
- Maximum building height
- Plot ratio (how much floor space relative to land area)
- Site coverage limits
- Minimum lot sizes for subdivision

### Site Characteristics
Developers pay more for sites that are:
- **Flat or gently sloping** (reduces construction costs)
- **Regular shaped** (rectangular is ideal)
- **Wide frontage** (allows better building design and access)
- **Corner blocks** (dual frontage, better apartment layouts)
- **North-facing** (premium for residential end-buyers)

### Location Fundamentals
Strong end-buyer demand translates directly to higher GRV, which means developers can pay more for your land:
- Walking distance to train stations or busways
- Close to employment centres, universities, hospitals
- Near retail, cafes, and lifestyle amenities
- In suburbs with strong demographic growth

### Existing Approvals
A site with a current Development Approval (DA) typically commands a 10-20% premium over an unapproved site. The developer avoids the time, cost, and risk of the approval process.

## What Reduces Development Value

- **Contamination** or suspected contamination (former service stations, industrial uses)
- **Flood overlays** requiring elevated construction
- **Heritage overlays** restricting demolition or design
- **Easements** through the building envelope reducing developable area
- **Steep topography** increasing construction complexity
- **Poor access** (narrow driveways, no rear lane access)

## How to Get an Accurate Valuation

A standard residential valuation won't capture development potential. You need either:

1. **A development site appraisal** from a specialist agent who understands the developer market
2. **A feasibility study** prepared by a quantity surveyor and town planner
3. **Developer feedback** — an experienced agent can quietly test the market before you commit to selling

At ACRES, we provide confidential development site appraisals at no cost. We assess your property's zoning, constraints, and realistic development yield to give you an informed view of what the market will pay.

## Frequently Asked Questions

**Q: How much more is my property worth as a development site?**
It depends on zoning and location. In inner-city Brisbane, a house on 800m² zoned for medium density could be worth 50-100% more to a developer than to a home buyer. Corner blocks and sites near transport can see even greater premiums.

**Q: Do I need to get a DA before selling?**
Not necessarily. Many developers prefer to purchase sites without approval so they can design their own project. However, having a DA can increase the price and speed up the sale.

**Q: How long does it take to sell a development site?**
Development site sales typically take 3-6 months from listing to unconditional contract, depending on the sale method and market conditions. Settlement may be extended to allow for finance or DA conditions.

---

*ACRES provides complimentary development site appraisals across Brisbane and South East Queensland. Call 07 3096 0542 or email daniel@acres.au for a confidential assessment.*
    `,
    faq: [
      { q: 'How much more is my property worth as a development site?', a: 'In inner-city Brisbane, a house on 800m² zoned for medium density could be worth 50-100% more to a developer than to a home buyer. Corner blocks near transport can see even greater premiums.' },
      { q: 'Do I need a DA before selling my development site?', a: 'Not necessarily. Many developers prefer to purchase without approval so they can design their own project. However, having a DA can increase the price and speed up the sale.' },
      { q: 'How long does it take to sell a development site?', a: 'Development site sales typically take 3-6 months from listing to unconditional contract, with settlement often extended for finance or DA conditions.' },
      { q: 'How do I find out if my property has development potential?', a: 'Check your council zoning map to see if your property is zoned medium or high density residential. ACRES provides free development site assessments — call 07 3096 0542 for a confidential appraisal.' },
      { q: 'What is residual land value and how does it affect my price?', a: 'Residual land value is calculated by taking the Gross Realisation Value of a completed project minus construction costs and developer margin. This is how developers determine what they can pay for your land.' },
      { q: 'Can I stay in my home while the development approval runs?', a: 'Yes. Many development site contracts include a settlement period of 12-24 months, allowing vendors to remain in their home while the developer obtains approvals.' },
      { q: 'Should I sell to the first developer who approaches me?', a: 'No. Creating competitive tension between 3-5 qualified developers typically increases sale price by 10-20%. ACRES runs competitive campaigns to maximise your result.' },
      { q: 'What is the minimum block size developers look for in Brisbane?', a: 'Most developers target blocks of 600m²+ for duplexes and 800m²+ for townhouses. However, corner blocks of 500m² can also be attractive due to dual frontage advantages.' },
    ],
  },
  {
    id: 'should-i-sell-my-property-as-a-development-site',
    category: 'Seller Guide',
    title: 'Should I Sell My Property to a Developer? Brisbane Seller\'s Guide 2026',
    excerpt: 'Five signs your property might be worth more to a developer than a home buyer.',
    date: '2026-04-19',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1514897431180-fa36ff71d081?w=800&q=85',
    icon: HelpCircle,
    metaDescription: 'Should you sell your property as a development site? Learn the signs that your land has development potential and how to determine if selling to a developer is the right choice.',
    keyFacts: [
      'Five signs your property may be a development site: zoned MDR/HDR, lot size 600m2+, near transport, corner position, or in a growth suburb',
      'Developers in Brisbane are actively paying 20-80% above residential market value for sites with potential',
      'Even older homes in poor condition can have significant development value — it is the land that matters',
      'A free site appraisal from ACRES can determine your property\'s true development potential in 24-48 hours',
      'You may be able to stay in your home during the conditional contract period — this is common in SEQ',
    ],
    content: `
## Is Your Property a Development Site?

Not every property has development potential. But if your home sits on a block that ticks certain boxes, selling to a developer could unlock significantly more value than a traditional residential sale.

Here are five signs your property might be worth more as a development site.

## Sign 1: Your Zoning Allows Higher Density

This is the most important factor. If your property is zoned for anything above low-density residential, it likely has development potential. In Brisbane, key zones to look for include:

- **Low-Medium Density Residential (LMR)**: Allows duplexes, townhouses, and small lot housing
- **Medium Density Residential (MDR)**: Allows walk-up apartments (3-5 storeys)
- **High Density Residential (HDR)**: Allows apartment buildings (6+ storeys)
- **Centre zones**: Allow mixed-use development combining residential, commercial, and retail

You can check your property's zoning on your local council's online mapping tool. In Brisbane, this is the [City Plan Interactive Mapping](https://cityplan.brisbane.qld.gov.au/).

## Sign 2: You're on a Large or Irregular Block

Developers need minimum site areas to achieve viable development yields. Generally:

- **Townhouses**: 600m²+ (for 3-4 dwellings)
- **Walk-up apartments**: 800m²+ (for 8-12 apartments)
- **Mid-rise apartments**: 1,000m²+ (for 20+ apartments)

If your block is larger than typical for your suburb, or if you're on an irregular shaped block that's less desirable for a home buyer but works well for a developer's design, the development premium could be substantial.

## Sign 3: You're on a Corner Block

Corner blocks are gold for developers. Dual street frontage allows for:

- Better apartment layouts with more natural light
- Separate pedestrian and vehicle access
- More attractive building forms that address both streets
- Higher density allowances under some planning schemes

If you own a corner block in a medium-to-high density zone, you almost certainly have development value above standard residential.

## Sign 4: You're Near New Infrastructure

Major infrastructure projects create development demand. Properties within walking distance of the following are highly sought by developers:

- **New or upgraded train stations** (especially Cross River Rail stations)
- **Bus rapid transit stations** (Brisbane Metro)
- **Major retail or commercial developments**
- **University or hospital expansions**

The closer you are to these catalysts, the more a developer will pay — because the end-buyers of the completed product will also pay a premium.

## Sign 5: Developers Have Already Approached You

If you've received unsolicited letters or knocks on the door from developers or their agents, it's a strong signal your property has development value. However, unsolicited approaches are often below market value because the developer is trying to avoid competition.

> **Pro tip**: Never accept an unsolicited developer offer without getting independent advice. An experienced development site agent can test the broader market and create competitive tension that drives a significantly higher price.

## How to Find Out for Sure

The fastest way to understand your property's development potential is to speak with a specialist development site agent. At ACRES, we provide a complimentary, no-obligation assessment that covers:

- Your property's zoning and what can be built
- Estimated development yield (number of dwellings)
- Current market demand for sites in your area
- Realistic price expectations based on recent comparable sales

## Frequently Asked Questions

**Q: Will selling to a developer take longer than a normal sale?**
Development site sales can take longer (3-6 months vs 4-8 weeks for residential), but the higher price often more than compensates. Some sales also involve extended settlement periods of 6-12 months.

**Q: Can I still live in my home while it's marketed as a development site?**
Yes. Most development site sales don't require you to vacate until settlement, which can be months or even years after the contract is signed (especially with conditional contracts).

**Q: What if my property isn't worth more as a development site?**
If the development value doesn't exceed the residential value, we'll tell you. There's no obligation, and you can proceed with a standard residential sale instead.

---

*Contact ACRES on 07 3096 0542 for a free development site assessment. We'll give you an honest view of whether your property has development potential.*
    `,
    faq: [
      { q: 'How do I know if my property is a development site?', a: 'Key indicators include medium-to-high density zoning, a large or corner block, proximity to new infrastructure, and unsolicited approaches from developers. Check your local council zoning map as a first step.' },
      { q: 'Will selling to a developer take longer than a normal sale?', a: 'Development site sales typically take 3-6 months vs 4-8 weeks for residential. But the significantly higher price usually compensates for the longer timeframe.' },
      { q: 'Can I live in my home while it is marketed as a development site?', a: 'Yes. Most development site sales allow you to stay until settlement, which can be months or years after the contract is signed.' },
      { q: 'What happens if the developer cannot get approval?', a: 'Most development contracts include a DA condition clause. If the developer fails to obtain approval, the contract can be terminated and you keep the deposit and your property.' },
      { q: 'Do I need to pay for a town planning report before selling?', a: 'Not necessarily, but it can be valuable. A professional planning report costs $2,000-$5,000 but can add $50,000-$200,000 to the sale price by demonstrating site potential to developers.' },
      { q: 'How do I know if ACRES can sell my property?', a: 'ACRES provides free, no-obligation property assessments across all of South East Queensland. Call 07 3096 0542 or visit acres.au to request a confidential site appraisal.' },
    ],
  },
  {
    id: 'what-developers-look-for-when-buying-your-land',
    category: 'Seller Guide',
    title: 'What Developers Look for When Buying Your Land in Brisbane',
    excerpt: 'Understanding the buyer\'s perspective helps you position your property for maximum value.',
    date: '2026-04-18',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1774780855191-3ab5f0b06932?w=800&q=85',
    icon: Eye,
    metaDescription: 'What do property developers look for when buying land? Understanding zoning, yield, feasibility, and site characteristics that make your property attractive to developers.',
    keyFacts: [
      'Top 5 factors developers assess: zoning code, lot dimensions, slope, flood/overlay status, and end-buyer demand',
      'Minimum lot size for duplex development in Brisbane is typically 400m2; for townhouses, 800m2+',
      'Corner blocks command 10-25% premiums due to dual frontage and flexible design options',
      'Developers prefer sites with existing DA approval — it reduces risk and speeds up project timelines',
      'Proximity to train stations, schools, and shopping adds 5-15% to development site values',
    ],
    content: `
## Thinking Like a Developer

When a developer looks at your property, they don't see a home — they see a mathematical equation. Understanding what drives their calculations helps you position your property for maximum value and anticipate the questions they'll ask.

## The Developer's Checklist

### 1. Zoning and Yield

The first thing any developer checks is the zoning. This determines:

- **What can be built** (houses, townhouses, apartments, mixed-use)
- **How high** (number of storeys)
- **How dense** (number of dwellings per hectare or plot ratio)
- **The yield** — how many saleable dwellings or lots can be achieved

A developer buying a 1,000m² site zoned for 5-storey apartments will pay dramatically more than for the same site zoned for townhouses — because the yield (and therefore revenue) is much higher.

### 2. Site Dimensions and Shape

Not all 800m² blocks are created equal. Developers prefer:

| Feature | Why It Matters |
|---|---|
| **Wide frontage (15m+)** | Allows efficient building design and compliant setbacks |
| **Regular rectangular shape** | Maximises usable area, simpler construction |
| **Flat topography** | Reduces earthworks and retaining wall costs |
| **Corner position** | Dual access, better apartment amenity, often higher density allowance |
| **North orientation** | Premium for residential end-buyers, easier compliance with amenity codes |

### 3. Constraints and Overlays

Developers will investigate every planning overlay on your property:

- **Flood overlay**: May require elevated ground floors, reducing ground-level yield
- **Heritage overlay**: Can prevent demolition or restrict design
- **Character overlay**: May limit height or require sympathetic design
- **Airport noise overlay**: Requires acoustic treatment, increasing construction costs
- **Vegetation protection**: May reduce developable area

The fewer constraints, the more a developer will pay.

### 4. Access and Services

Practical site considerations matter:

- **Vehicle access**: Sufficient width for a driveway and basement entry
- **Rear lane access**: Highly valued — separates pedestrian and vehicle entries
- **Sewer and stormwater**: Adequate capacity for the proposed development
- **Electricity**: Three-phase power availability for larger developments
- **Telecommunications**: NBN or fibre connectivity

### 5. Market Demand for the End Product

A developer won't buy your site unless they're confident the completed product will sell. They assess:

- Recent sales of comparable new product in the area
- Current supply pipeline (how many competing projects are underway)
- Demographic trends (who is buying in this suburb and what do they want?)
- Price sensitivity (what price point will the market support?)

### 6. Existing Improvements

Ironically, a rundown house on a large block can be more attractive to a developer than a renovated one — because:

- Demolition costs are relatively minor ($15,000-$30,000 for a standard house)
- A renovated house increases the seller's price expectations but doesn't change the development value
- Vacant or easily demolished sites allow faster project commencement

## How to Make Your Property More Attractive to Developers

1. **Know your zoning** and be able to articulate the development potential
2. **Get a survey** (boundary survey at minimum) — it saves the developer time and shows you're serious
3. **Resolve any title issues** (caveats, boundary disputes, unclear easements)
4. **Obtain preliminary planning advice** if possible — even a pre-lodgement meeting summary adds value
5. **Engage a specialist agent** who can present your site with a development lens, not just as a house

## Frequently Asked Questions

**Q: Do developers prefer cleared or occupied sites?**
Most developers are comfortable purchasing occupied sites with extended settlement terms. Cleared sites can attract a small premium due to faster project commencement, but it's rarely worth the expense of demolishing before sale.

**Q: Should I approach developers directly?**
You can, but you'll likely achieve a lower price. Developers making unsolicited offers are seeking below-market deals. A specialist agent creates competitive tension by exposing the site to multiple developers simultaneously.

**Q: What if my property has contamination issues?**
Contamination doesn't necessarily prevent a sale, but it will affect price. A Phase 1 environmental assessment ($3,000-$5,000) can quantify the issue and provide certainty for both parties.

---

*ACRES understands the developer mindset. We position your property to attract maximum competition and achieve the best possible price. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'What do developers look for when buying land?', a: 'Developers evaluate zoning and yield potential, site dimensions, planning constraints, access and services, and market demand for the end product. Wide, flat, regular-shaped blocks in medium-to-high density zones are most attractive.' },
      { q: 'Should I approach developers directly to sell my land?', a: 'Direct approaches typically achieve lower prices. A specialist agent creates competitive tension by exposing the site to multiple developers simultaneously, driving higher offers.' },
      { q: 'Does a renovated house increase development site value?', a: 'No. Development site value is based on what can be built, not what currently exists. A rundown house on a large block can actually be more attractive because the site is easier to redevelop.' },
      { q: 'What block size do developers want in Brisbane?', a: 'Minimum 400m² for duplexes, 600-800m² for townhouses, and 1,500m²+ for unit developments. Corner blocks and through-lots are preferred due to design flexibility.' },
      { q: 'Do overlays affect my development site value?', a: 'Yes. Flood, heritage, character, and vegetation overlays can restrict what can be built and reduce site value. Always check both zoning AND overlays before marketing your property.' },
      { q: 'How does frontage width affect development potential?', a: 'Most councils require minimum 15-18m frontage for townhouse development. Wider blocks (20m+) allow more design options and typically command higher prices.' },
    ],
  },
  {
    id: 'how-to-maximise-sale-price-of-development-site',
    category: 'Seller Guide',
    title: 'How to Maximise the Sale Price of Your Development Site in 2026',
    excerpt: 'Practical steps to prepare your property and create competitive tension among developer buyers.',
    date: '2026-04-17',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1557518016-95747c0eef63?w=800&q=85',
    icon: TrendingUp,
    metaDescription: 'How to maximise the sale price when selling your property as a development site. Tips on preparation, agent selection, sale methods, and creating competitive tension among developers.',
    keyFacts: [
      'Creating competitive tension between 3-5 qualified developers typically increases sale price by 10-20%',
      'Professional town planning advice before selling costs $2,000-$5,000 but can add $50,000-$200,000 to sale price',
      'Expressions of Interest (EOI) campaigns often achieve the best prices for development sites',
      'Timing matters: selling during Q1 and Q4 aligns with developer budget cycles and approval timelines',
      'ACRES has achieved record per-sqm prices for development sites across Brisbane through competitive tension strategies',
    ],
    content: `
## Getting the Best Price

Selling a development site is fundamentally different from selling a house. The buyer pool is smaller but more sophisticated, the due diligence is more complex, and the negotiation dynamics require specialist expertise. Here's how to position your property for maximum value.

## Step 1: Understand Your Property's True Potential

Before engaging any agent, understand what your property is worth as a development site:

- **Check the zoning** on your council's online mapping tool
- **Research recent development site sales** in your suburb (your agent should provide this)
- **Understand the maximum yield** — how many dwellings or lots could be built
- **Identify any constraints** that might limit development (overlays, easements, contamination)

An informed seller is a powerful negotiator. When you understand the numbers, you can assess offers with confidence.

## Step 2: Choose the Right Agent

This is the single most important decision you'll make. The wrong agent will cost you hundreds of thousands of dollars. Look for:

- **Specialist experience** in development site sales (not just residential)
- **A proven buyer database** of active developers in your area
- **Market intelligence** — they should be able to quote recent comparable sales and current market conditions
- **A clear marketing strategy** tailored to developer buyers
- **References** from past development site vendors

> **Warning**: General residential agents often undervalue development sites because they don't understand the developer market. A specialist agent consistently achieves 15-30% more than a generalist.

## Step 3: Prepare Your Property

Invest in these items before going to market — they pay for themselves many times over:

### Essential Preparation
- **Current survey plan** ($2,000-$4,000): Shows boundaries, area, levels, and existing structures. Every developer needs this.
- **Title search and plan of survey**: Confirm ownership, easements, and encumbrances
- **Services information**: Water, sewer, stormwater, and electrical capacity

### Valuable Additions
- **Town planning assessment** ($3,000-$5,000): Professional opinion on what can be built, including yield estimate and any planning risks
- **Concept design** ($5,000-$10,000): An architect's preliminary scheme showing how the site could be developed. This makes the opportunity tangible for buyers.
- **Environmental assessment** (if applicable): Removes contamination uncertainty

### Don't Bother With
- Major house renovations (development value isn't based on the existing house)
- Landscaping or cosmetic improvements (developers will demolish regardless)
- Getting your own DA (unless you're confident in the design and have the time/money — a poorly conceived DA can actually reduce value)

## Step 4: Select the Right Sale Method

| Method | Best For | Pros | Cons |
|---|---|---|---|
| **Expressions of Interest (EOI)** | Most development sites | Creates urgency, allows flexible terms, attracts competitive offers | Requires strong agent network to generate interest |
| **Private Treaty** | Smaller sites, known buyer pool | Flexible negotiation, no deadline pressure | Less competitive tension |
| **Auction** | Sites with broad appeal | Transparent process, creates urgency | Less common for dev sites, can deter conditional buyers |
| **Off-Market** | Sensitive vendors, unique sites | Privacy, targeted approach | Smaller buyer pool, potentially lower price |

For most development sites, an **EOI campaign** achieves the best balance of competitive tension and deal flexibility.

## Step 5: Create Competitive Tension

The biggest driver of price is competition between buyers. Your agent should:

1. **Contact their developer database** directly with the opportunity
2. **Conduct site inspections** with qualified parties
3. **Provide an information memorandum** with survey, zoning, planning assessment, and concept design
4. **Set a deadline** for offers (EOI closing date)
5. **Negotiate** with the strongest offers to maximise price and terms

Multiple interested parties competing for the same site consistently achieves higher prices than negotiating with a single buyer.

## Step 6: Negotiate Terms, Not Just Price

The highest price isn't always the best deal. Key terms to negotiate include:

- **Deposit amount and structure** (5-10% is standard; more is better for security)
- **Conditions** (DA conditions, finance conditions, and their timeframes)
- **Settlement period** (longer settlement gives you time to plan your next move)
- **Access for due diligence** (when and how often the buyer can access the site)
- **Sunset clauses** (deadlines for conditions to be satisfied)

A skilled agent will structure the deal to maximise your security and certainty while achieving the best price.

## Frequently Asked Questions

**Q: How much does it cost to prepare a development site for sale?**
Budget $5,000-$15,000 for survey, title search, and planning assessment. A concept design adds $5,000-$10,000 but often increases the sale price by significantly more than it costs.

**Q: Should I accept a conditional offer or wait for an unconditional one?**
Conditional offers (typically subject to DA) are standard for development sites. A strong conditional offer at a high price is often better than a lower unconditional offer. Your agent should assess the buyer's track record and the likelihood of conditions being satisfied.

**Q: What commission do agents charge for development site sales?**
Commission rates for development sites are typically 1.5-2.5% of the sale price, sometimes with a minimum fee. Given the specialist expertise required and the higher sale prices achieved, this represents strong value.

---

*ACRES has a proven track record of maximising development site sale prices across Brisbane. Call Daniel McCormack on 07 3096 0542 for a confidential discussion.*
    `,
    faq: [
      { q: 'How do I maximise the sale price of my development site?', a: 'Prepare your property with a current survey and planning assessment, choose a specialist development site agent, use an Expressions of Interest campaign to create competitive tension, and negotiate terms as well as price.' },
      { q: 'How much does it cost to prepare a development site for sale?', a: 'Budget $5,000-$15,000 for survey, title search, and planning assessment. A concept design adds $5,000-$10,000 but often increases the sale price by significantly more than it costs.' },
      { q: 'What commission do agents charge for development site sales?', a: 'Commission rates are typically 1.5-2.5% of the sale price. Given the specialist expertise and higher prices achieved versus generalist agents, this represents strong value.' },
    ],
  },
  {
    id: 'understanding-zoning-property-value',
    category: 'Guide',
    title: 'Understanding Property Zoning in Brisbane: What Your Zoning Code Means for Value',
    excerpt: 'A plain-English guide to how zoning works and why it\'s the single biggest factor in your land\'s development value.',
    date: '2026-04-16',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1760120351693-e8418f79952f?w=800&q=85',
    icon: Scale,
    metaDescription: 'Understanding property zoning in Australia. How zoning affects your land value, what different zones mean, and how to check your property zoning in Brisbane and Queensland.',
    keyFacts: [
      'Zoning is the single biggest factor determining development site value in Queensland',
      'LDR (Low Density Residential) allows houses and duplexes; MDR allows townhouses; HDR allows apartments',
      'You can check your zoning free at Brisbane City Council\'s City Plan Online interactive mapping tool',
      'Rezoning from LDR to MDR can increase land value by 50-200% depending on location',
      'Overlays (flood, heritage, character) can restrict development — always check both zoning AND overlays',
    ],
    content: `
## What is Zoning?

Zoning is how local councils regulate what can be built on every piece of land. Your property's zoning determines whether it can be used for residential, commercial, industrial, or mixed purposes — and critically, **how intensive** that use can be.

For property owners, zoning is the single biggest determinant of land value. A change in zoning can increase a property's value by hundreds of thousands or even millions of dollars.

## Brisbane Residential Zones Explained

Under the Brisbane City Plan 2014, the main residential zones are:

### Low Density Residential
- **What you can build**: Detached houses, granny flats
- **Height limit**: 8.5m (2 storeys)
- **Impact on value**: Standard residential value only. Limited development potential.

### Character Residential
- **What you can build**: Detached houses, some duplexes in specific areas
- **Height limit**: 9.5m (2 storeys), strict character controls
- **Impact on value**: May restrict development. Demolition of pre-1947 houses often restricted.

### Low-Medium Density Residential (LMR)
- **What you can build**: Townhouses, duplexes, triplexes, small lot housing
- **Height limit**: 9.5m (2-3 storeys)
- **Impact on value**: Moderate development premium. Sites 600m²+ become attractive to developers.

### Medium Density Residential (MDR)
- **What you can build**: Walk-up apartments, townhouses
- **Height limit**: 15m (4-5 storeys typical)
- **Impact on value**: Significant development premium. The sweet spot for many Brisbane development sites.

### High Density Residential (HDR)
- **What you can build**: Apartment buildings
- **Height limit**: Varies (often 30m+ / 8-15+ storeys)
- **Impact on value**: Maximum development premium. Sites in HDR zones command the highest prices.

## How Zoning Affects Your Land Value

Consider three identical 800m² blocks in the same Brisbane suburb:

| Zoning | Potential Yield | Estimated Land Value |
|---|---|---|
| Low Density | 1 house | $900,000 (residential) |
| Low-Medium Density | 4 townhouses | $1,200,000 - $1,500,000 |
| Medium Density | 12 apartments | $1,800,000 - $2,500,000 |

Same block size, same suburb — but the zoning creates dramatically different values. This is why checking your zoning is the first step in understanding your property's true worth.

## Neighbourhood Plans and Overlays

Your property's zoning is only part of the story. Brisbane also applies:

### Neighbourhood Plans
Local area plans that may provide additional building height, density, or design requirements specific to your suburb. Some neighbourhood plans allow greater height than the base zoning would suggest.

### Overlays
Additional planning layers that apply to specific properties:
- **Flood overlay**: May require elevated construction
- **Heritage overlay**: May prevent demolition
- **Character overlay**: May limit design freedom
- **Infrastructure overlay**: May require land dedication for roads or utilities

## How to Check Your Zoning

### Brisbane
1. Visit [Brisbane City Plan Interactive Mapping](https://cityplan.brisbane.qld.gov.au/)
2. Search your address
3. Check the "Zoning" layer for your base zone
4. Check "Overlays" for any additional constraints
5. Check "Neighbourhood Plans" for local provisions

### Other Queensland Councils
Most councils have similar online mapping tools. Search "[your council name] planning scheme interactive mapping."

## Could Your Zoning Change?

Zoning isn't permanent. Councils periodically review their planning schemes, and individual properties can be rezoned through:

- **Council-initiated amendments** (often in response to population growth or infrastructure investment)
- **State government planning directives** (such as transit-oriented development policies)
- **Owner-initiated rezoning applications** (expensive and uncertain, but possible)

Properties near new infrastructure (like Cross River Rail stations) are prime candidates for future rezoning to higher density — which would significantly increase their value.

## Frequently Asked Questions

**Q: How do I check my property's zoning?**
Use your local council's online planning scheme mapping tool. In Brisbane, visit cityplan.brisbane.qld.gov.au and search your address. The zoning layer shows your property's classification.

**Q: Can I apply to have my property rezoned?**
Technically yes, but owner-initiated rezonings are expensive, time-consuming, and uncertain. It's usually only viable for large sites or groups of properties. Most rezoning happens through council-initiated planning scheme amendments.

**Q: Does a higher zoning guarantee a higher sale price?**
Higher zoning means greater development potential, which generally translates to a higher price. However, other factors like site constraints, market conditions, and demand for the end product also play important roles.

---

*Not sure what your zoning means for your property's value? ACRES provides free development site assessments. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'How do I check my property zoning?', a: 'Use your local council online planning scheme mapping tool. In Brisbane, visit cityplan.brisbane.qld.gov.au and search your address. The zoning layer shows your property classification.' },
      { q: 'Can I apply to have my property rezoned?', a: 'Technically yes, but owner-initiated rezonings are expensive and uncertain. Most rezoning happens through council-initiated planning scheme amendments, often triggered by infrastructure investment.' },
      { q: 'What zoning is most valuable for development?', a: 'High Density Residential and Medium Density Residential zones command the highest development premiums. Even Low-Medium Density can significantly increase value for sites over 600m².' },
    ],
  },
  {
    id: 'development-site-sales-process-what-to-expect',
    category: 'Seller Guide',
    title: 'Development Site Sales Process in Australia: What to Expect as a Seller',
    excerpt: 'A step-by-step timeline from listing to settlement when selling your property to a developer.',
    date: '2026-04-15',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1672626881472-b96d3eb5445e?w=800&q=85',
    icon: Clock,
    metaDescription: 'What to expect when selling a development site in Australia. Timeline from listing to settlement, sale methods, conditional contracts, and key milestones explained.',
    content: `
## Overview

Selling a development site follows a different process than selling a house. The buyers are professionals, the due diligence is more extensive, and the contracts are more complex. Here's what to expect at each stage.

## Stage 1: Pre-Market Preparation (2-4 Weeks)

Before your property hits the market, your agent will:

- Commission a **boundary survey** (if you don't have a recent one)
- Obtain a **current title search** and review for any issues
- Prepare a **planning summary** outlining zoning, height, and development potential
- Optionally commission a **concept design** from an architect
- Prepare an **information memorandum** — the marketing document sent to developers
- Compile a **target buyer list** from their developer database

This preparation phase is critical. A well-prepared site attracts higher-quality offers and faster decision-making from buyers.

## Stage 2: Marketing Campaign (4-6 Weeks)

Your agent will typically run an **Expressions of Interest (EOI)** campaign:

- **Week 1**: Direct contact with developer database, email campaign, listing on commercial portals
- **Weeks 2-4**: Site inspections with interested parties, responding to information requests
- **Weeks 4-6**: Offer deadline approaches, buyers finalise their feasibility analysis and submit offers

During this period, expect your agent to provide weekly updates on enquiry levels, inspection feedback, and any early offers.

## Stage 3: Offer Assessment and Negotiation (1-2 Weeks)

After the EOI deadline, your agent will:

1. **Present all offers** in a comparative format
2. **Assess offer quality** — not just price, but conditions, deposit, settlement terms, and buyer track record
3. **Shortlist** the strongest 2-3 offers
4. **Negotiate** with shortlisted buyers to improve price and terms
5. **Recommend** the best overall offer for your circumstances

The highest price isn't always the best offer. A slightly lower unconditional offer from a proven developer may be more valuable than a higher conditional offer from an unknown buyer.

## Stage 4: Contract Exchange (1-2 Weeks)

Once you accept an offer, lawyers prepare the contract of sale. For development sites, this typically includes:

- **Standard conditions**: Finance, building and pest (sometimes waived)
- **Special conditions**: DA condition, amalgamation condition, contamination investigation
- **Extended settlement**: Often 6-12 months to allow for DA and finance
- **Sunset clause**: A deadline by which all conditions must be satisfied

Contract exchange is the point at which the deposit (typically 5-10% of the purchase price) is paid.

## Stage 5: Conditional Period (3-12 Months)

If the contract is conditional (most development site contracts are), the buyer will work through their conditions:

- **Due diligence investigations** (contamination, geotechnical, services)
- **Development application preparation and lodgement**
- **Council assessment and approval**
- **Finance approval**

During this period, you remain the legal owner and can continue to live in the property. The buyer may request periodic access for investigations.

## Stage 6: Settlement (1-4 Weeks After Conditions Met)

Once all conditions are satisfied, the contract goes unconditional and settlement is arranged:

- **Pre-settlement inspection** by the buyer
- **Financial settlement** conducted electronically (PEXA in Queensland)
- **Transfer of ownership** registered with the Titles Office
- **Keys and access** handed to the buyer

You'll need to have vacated the property by the settlement date.

## Typical Timeline Summary

| Stage | Duration |
|---|---|
| Pre-market preparation | 2-4 weeks |
| Marketing campaign | 4-6 weeks |
| Offer assessment & negotiation | 1-2 weeks |
| Contract exchange | 1-2 weeks |
| Conditional period | 3-12 months |
| Settlement | 1-4 weeks |
| **Total** | **5-15 months** |

## Frequently Asked Questions

**Q: Can I stay in my home during the sales process?**
Yes. You can remain in the property through marketing, negotiation, and the conditional period. You only need to vacate by the settlement date.

**Q: What happens if the buyer's DA is refused?**
If the contract has a DA condition and approval is refused, the buyer can terminate and their deposit is refunded. You keep the property and can relist it.

**Q: Do I pay the agent's commission at settlement?**
Yes. Agent commission is typically deducted from the sale proceeds at settlement, along with legal fees and any other selling costs.

---

*For a detailed assessment of your property's development potential and expected timeline, contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'How long does it take to sell a development site?', a: 'The typical timeline is 5-15 months from preparation to settlement. Marketing takes 4-6 weeks, negotiation 1-2 weeks, and the conditional period (usually for DA approval) can be 3-12 months.' },
      { q: 'Can I stay in my home while selling to a developer?', a: 'Yes. You can remain in the property through marketing, negotiation, and the conditional period. You only need to vacate by the settlement date.' },
      { q: 'What happens if the buyer DA is refused?', a: 'If the contract has a DA condition and approval is refused, the buyer can terminate and their deposit is refunded. You keep the property and can relist it.' },
    ],
  },
  {
    id: 'conditional-contracts-explained-sellers-guide',
    category: 'Seller Guide',
    title: 'Conditional Contracts Explained: What Property Sellers Need to Know in 2026',
    excerpt: 'Why most development site sales involve conditional contracts and how to protect your interests.',
    date: '2026-04-14',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1760120351219-e51b53ce428f?w=800&q=85',
    icon: FileCheck,
    metaDescription: 'Understanding conditional contracts in development site sales. What conditions to expect, how to negotiate, sunset clauses, and how to protect yourself as a vendor.',
    content: `
## What is a Conditional Contract?

A conditional contract is an agreement to purchase property that is subject to certain conditions being met before it becomes binding. If the conditions aren't satisfied within the agreed timeframe, either party can walk away.

In development site sales, conditional contracts are the norm — not the exception. Understanding how they work protects you from risk while allowing you to achieve the best possible price.

## Common Conditions in Development Site Sales

### Development Approval (DA) Condition
The most common condition. The buyer has a specified period (typically 6-12 months) to obtain council approval for their proposed development. If approval is refused or not obtained within the timeframe, the buyer can terminate.

### Finance Condition
The buyer has 14-30 days to obtain formal finance approval. Standard in most property transactions.

### Due Diligence Condition
Allows the buyer 14-30 days to investigate the property — title, contamination, geotechnical, services, and planning constraints. If they discover something unacceptable, they can terminate.

### Amalgamation Condition
If the buyer is purchasing multiple adjacent properties to create a larger site, the contract may be conditional on all properties being secured.

## How to Protect Yourself

### 1. Set Clear Timeframes
Every condition should have a specific deadline. Open-ended conditions leave you exposed. Typical timeframes:
- Due diligence: 14-21 days
- Finance: 21-30 days
- DA: 6-12 months (depending on complexity)

### 2. Require a Substantial Deposit
A larger deposit demonstrates the buyer's commitment and provides security if they default. Standard is 5-10% of the purchase price, but 10% is preferable.

### 3. Include a Sunset Clause
A sunset clause sets an absolute deadline by which all conditions must be satisfied and the contract must go unconditional. If this date passes without conditions being met, either party can terminate.

### 4. Negotiate a Non-Refundable Deposit Component
In some cases, you can negotiate that a portion of the deposit (say 1-2%) becomes non-refundable after the due diligence period. This compensates you for keeping the property off the market during the conditional period.

### 5. Retain the Right to Continue Marketing
Some vendors negotiate the right to continue accepting backup offers during the conditional period. This creates additional pressure on the buyer to satisfy conditions quickly.

## The Risk-Reward Trade-Off

Conditional contracts introduce risk — the buyer may not proceed. But they also typically achieve higher prices:

| Contract Type | Typical Price | Risk Level |
|---|---|---|
| Unconditional | Lower (5-15% less) | No risk |
| Conditional on DA | Higher (market rate) | Moderate |
| Conditional on DA + Finance | Highest potential | Higher |

The premium for accepting conditions reflects the time and opportunity cost of having your property tied up during the conditional period.

## When to Accept vs Reject Conditions

**Accept conditions when:**
- The price justifies the risk and waiting period
- The buyer has a strong track record of completing similar projects
- The conditions have clear timeframes and sunset clauses
- Your financial situation allows for an extended timeline

**Push back on conditions when:**
- The buyer's track record is unproven
- The timeframes are unreasonably long
- Multiple conditions create compounding risk
- You need certainty and a quick settlement

## Frequently Asked Questions

**Q: What happens to my deposit if the buyer terminates under a condition?**
If the buyer terminates within the terms of an agreed condition (e.g., DA refused within the DA condition period), the deposit is typically refunded in full.

**Q: Can I sell to someone else during the conditional period?**
Generally no — you're contracted to the buyer during the conditional period. However, you can negotiate backup offer rights in the contract.

**Q: How do I assess whether a conditional buyer is genuine?**
Your agent should investigate the buyer's track record: have they completed similar projects? Do they have established relationships with planners, architects, and financiers? A proven developer is much more likely to satisfy conditions.

---

*ACRES negotiates development site contracts that protect vendor interests while achieving premium prices. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'What is a conditional contract for a development site?', a: 'A conditional contract is subject to certain conditions (typically DA approval, finance, and due diligence) being met before it becomes binding. This is standard for development site sales.' },
      { q: 'What happens to my deposit if the buyer walks away?', a: 'If the buyer terminates within the terms of an agreed condition, the deposit is typically refunded. If they default outside the conditions, you may be entitled to retain the deposit.' },
      { q: 'How long is a typical conditional period?', a: 'Due diligence conditions are typically 14-21 days, finance 21-30 days, and DA conditions 6-12 months depending on the complexity of the proposed development.' },
    ],
  },
  {
    id: 'tax-implications-selling-development-site-australia',
    category: 'Guide',
    title: 'Tax Implications of Selling a Development Site in Australia: CGT & GST Guide',
    excerpt: 'CGT, GST, and margin scheme basics every development site vendor should understand.',
    date: '2026-04-13',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1757157295150-d9a04b8bb9cf?w=800&q=85',
    icon: Calculator,
    metaDescription: 'Tax implications of selling a development site in Australia. Capital gains tax, GST, margin scheme, and tax concessions explained for property vendors.',
    content: `
## Important Disclaimer

This article provides general information only and is not tax advice. Every property sale has unique circumstances. Always consult a qualified tax professional before making decisions about selling a development site.

## Capital Gains Tax (CGT)

When you sell a property for more than you paid for it, the profit (capital gain) is generally subject to Capital Gains Tax. Key points for development site sellers:

### How CGT is Calculated

**Capital Gain = Sale Price − Cost Base**

Your cost base includes:
- Original purchase price
- Stamp duty paid on purchase
- Legal fees (purchase and sale)
- Agent's commission on sale
- Capital improvements made during ownership
- Incidental costs of buying and selling

### The 50% CGT Discount

If you've owned the property for more than 12 months (which most development site sellers have), you're entitled to a **50% discount** on the capital gain. This means only half the gain is added to your taxable income.

**Example:**
- Sale price: $1,500,000
- Cost base: $500,000
- Capital gain: $1,000,000
- After 50% discount: $500,000 added to taxable income

### Main Residence Exemption

If the property has been your main residence for the entire period you owned it, the capital gain may be fully exempt from CGT. If it was your main residence for part of the time, a partial exemption applies.

This is particularly relevant for homeowners selling their family home as a development site — you may pay no CGT at all.

## GST Considerations

### When GST Applies

The sale of residential property is generally **GST-free** if:
- It's been used solely for residential purposes
- The seller is not carrying on an enterprise of property development

However, GST may apply if:
- You're registered (or required to be registered) for GST
- The sale is considered part of an enterprise or profit-making scheme
- The property is new residential premises (which development sites generally aren't when sold by original owners)

### The Margin Scheme

If GST does apply to your sale, the **margin scheme** can significantly reduce the GST liability. Under the margin scheme:

- GST is calculated on the **margin** (sale price minus purchase price) rather than the full sale price
- Both buyer and seller must agree to use the margin scheme
- The GST amount is: Margin ÷ 11

**Example (without margin scheme):**
- Sale price: $1,500,000
- GST: $1,500,000 ÷ 11 = $136,364

**Example (with margin scheme):**
- Sale price: $1,500,000, purchased for $500,000
- Margin: $1,000,000
- GST: $1,000,000 ÷ 11 = $90,909

The margin scheme saves $45,455 in this example. Developers often request the margin scheme in their offers.

## Structuring the Sale

How you structure the sale can have significant tax implications:

- **Selling as an individual**: Subject to CGT at personal marginal tax rates (with 50% discount if held 12+ months)
- **Selling through a trust**: May allow distribution of the capital gain to beneficiaries in lower tax brackets
- **Selling through a company**: No 50% CGT discount, but company tax rate applies (25-30%)
- **Selling through an SMSF**: Concessional tax rates may apply (15% during accumulation, 0% during pension phase)

The right structure depends on your personal circumstances. Get advice from a tax professional before committing to a sale.

## Key Tax Planning Tips

1. **Engage a tax advisor early** — before you list the property, not after you've signed a contract
2. **Understand your CGT cost base** — gather all records of purchase costs, improvements, and holding costs
3. **Consider the timing** — selling in a financial year where your other income is lower can reduce the effective tax rate
4. **Check the main residence exemption** — if the property was ever your home, you may be entitled to a full or partial exemption
5. **Discuss the margin scheme** with your agent — developers may factor this into their offers

## Frequently Asked Questions

**Q: Do I pay CGT on my family home if I sell it as a development site?**
If the property has been your main residence for the entire period of ownership, the capital gain is generally exempt from CGT under the main residence exemption. Partial exemptions apply if it was your main residence for only part of the ownership period.

**Q: Will I have to pay GST on the sale?**
Most homeowners selling their residence as a development site won't be liable for GST. However, if you're GST-registered or the sale is part of an enterprise, GST may apply. Consult a tax advisor.

**Q: When do I need to pay CGT on the sale?**
CGT is reported in your tax return for the financial year in which the contract settles (not when the contract is signed). The tax is then payable as part of your normal income tax assessment.

---

*This article is general information only. ACRES recommends consulting a qualified tax professional before selling a development site. For property advisory, call 07 3096 0542.*
    `,
    faq: [
      { q: 'Do I pay capital gains tax when selling my home as a development site?', a: 'If the property has been your main residence for the entire ownership period, the gain is generally exempt from CGT. Partial exemptions apply for part-time residences. Always consult a tax professional.' },
      { q: 'Does GST apply when selling a development site?', a: 'Most homeowners selling their residence as a development site will not be liable for GST. However, if you are GST-registered or the sale is part of an enterprise, GST may apply.' },
      { q: 'What is the margin scheme for development site sales?', a: 'The margin scheme calculates GST on the margin (sale price minus purchase price) rather than the full sale price, significantly reducing the GST liability. Both buyer and seller must agree to use it.' },
    ],
  },
  {
    id: 'what-is-a-put-and-call-option-sellers-guide',
    category: 'Seller Guide',
    title: 'What Is a Put and Call Option in Property? Seller\'s Guide for 2026',
    excerpt: 'How option agreements work in development site transactions and what you need to know as a vendor.',
    date: '2026-04-12',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1761501668042-ab0f517aaf5a?w=800&q=85',
    icon: Handshake,
    metaDescription: 'What is a put and call option in property? A sellers guide to option agreements in development site transactions, how they work, and what to negotiate.',
    content: `
## What is a Put and Call Option?

A put and call option is a two-part agreement used in property transactions that gives:

- **The buyer (call option)**: The right, but not obligation, to purchase the property at an agreed price within a set timeframe
- **The seller (put option)**: The right to force the sale to the buyer if the buyer hasn't exercised their call option by the deadline

This structure is commonly used in development site transactions because it gives the developer time to obtain DA approval and finance before committing to the purchase.

## How It Works in Practice

### Step 1: Option Agreement
The developer pays an **option fee** (typically 1-3% of the agreed purchase price) for the right to purchase your property within a defined period (often 12-18 months).

### Step 2: Option Period
During this period, the developer pursues DA approval, conducts due diligence, and arranges finance. You continue to own and occupy the property.

### Step 3: Exercise or Expiry
At the end of the option period:
- If the developer **exercises the call option**, a binding contract of sale is created at the pre-agreed price
- If the developer **doesn't exercise**, you can exercise the **put option** to force the sale, or the option simply expires
- If neither party exercises, the option lapses and you keep the option fee

## Advantages for Sellers

- **Upfront option fee**: You receive a payment (usually non-refundable) regardless of whether the sale proceeds
- **Agreed price**: The purchase price is locked in upfront, protecting you from market falls
- **Put option protection**: If the developer doesn't proceed after obtaining DA, you can force the sale
- **Continued occupation**: You remain in the property during the option period
- **Limited obligation**: Until the option is exercised, there's no binding contract of sale

## Disadvantages for Sellers

- **Property is tied up**: While the option is on foot, you can't sell to anyone else
- **Opportunity cost**: If the market rises significantly, you're locked into the agreed price
- **Stamp duty timing**: The option fee may have stamp duty implications
- **Complexity**: Option agreements are more complex than standard contracts and require specialist legal advice

## Key Terms to Negotiate

| Term | What to Push For |
|---|---|
| **Option fee** | 2-3% of purchase price (non-refundable) |
| **Option period** | As short as practical (12 months is common) |
| **Purchase price** | Fixed and agreed upfront with no adjustments |
| **Put option** | Include a put option so you can force the sale |
| **Extension** | If the developer needs more time, require an additional fee |
| **Access** | Limit access to the property during the option period |

## Put and Call vs Conditional Contract

Both structures serve a similar purpose — allowing the developer time to obtain approvals before committing. The key differences:

| | Put and Call Option | Conditional Contract |
|---|---|---|
| **Binding contract?** | Not until option exercised | Yes, from exchange (subject to conditions) |
| **Stamp duty** | Deferred until exercise | Payable at exchange |
| **Deposit** | Option fee (smaller) | Full deposit (5-10%) |
| **Seller's security** | Option fee only | Full deposit held in trust |
| **Flexibility** | More complex, more flexible | Simpler, more standard |

Your solicitor and agent will advise on which structure best suits your situation.

## Frequently Asked Questions

**Q: Is the option fee refundable?**
The option fee is typically non-refundable. Even if the developer decides not to exercise the option, you keep the fee. This compensates you for keeping the property off the market during the option period.

**Q: What happens if I want to sell to someone else during the option period?**
You generally can't sell to another party while the option is on foot. The option agreement gives the developer exclusive rights during the option period.

**Q: Do I need a lawyer for a put and call option?**
Absolutely. Option agreements are complex legal documents with significant financial implications. Engage an experienced property solicitor who understands development transactions.

---

*ACRES can advise on the best transaction structure for your development site sale. Call 07 3096 0542 for a confidential discussion.*
    `,
    faq: [
      { q: 'What is a put and call option in property?', a: 'A two-part agreement where the buyer gets the right to purchase at an agreed price within a set timeframe (call option), and the seller gets the right to force the sale if the buyer does not exercise (put option).' },
      { q: 'Is the option fee refundable?', a: 'The option fee is typically non-refundable. Even if the developer decides not to proceed, you keep the fee as compensation for keeping the property off the market.' },
      { q: 'How long is a typical option period?', a: 'Option periods for development sites are typically 12-18 months, allowing the developer time to obtain DA approval and arrange finance.' },
    ],
  },
  {
    id: 'how-long-does-it-take-to-sell-development-site',
    category: 'Seller Guide',
    title: 'How Long Does It Take to Sell a Development Site in Brisbane?',
    excerpt: 'Realistic timelines and what factors affect how quickly your development site sells.',
    date: '2026-04-11',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1575275402664-9afd2de538ab?w=800&q=85',
    icon: Clock,
    metaDescription: 'How long does it take to sell a development site in Australia? Realistic timelines from listing to settlement and factors that affect sale speed.',
    content: `
## Short Answer

From listing to unconditional contract: **2-4 months**. From listing to settlement (including conditional periods): **6-18 months**. But timelines vary significantly based on several factors.

## Factors That Affect Timeline

### Site Quality and Pricing
Well-located sites at realistic prices attract strong interest quickly. Overpriced sites sit on the market and lose momentum. The first 4-6 weeks of marketing are critical — that's when developer attention is highest.

### Market Conditions
In a strong market with active developer demand, sites sell faster and at higher prices. In quieter periods, expect longer marketing campaigns and more negotiation.

### Zoning Clarity
Sites with clear, unambiguous zoning sell faster than those requiring interpretation or rezoning. Developers value certainty.

### Preparation Quality
A well-prepared site (with survey, planning assessment, and concept design) attracts faster offers because developers can run their feasibility immediately.

### Contract Structure
- **Unconditional contracts**: Fastest to settlement (4-8 weeks after exchange)
- **Conditional on DA**: Adds 6-12 months for the approval process
- **Put and call option**: 12-18 month option period before settlement

### Sale Method
- **Off-market**: Can be fast (2-4 weeks) if the right buyer is found, but no guarantee
- **EOI campaign**: 4-6 weeks marketing plus 1-2 weeks negotiation
- **Private treaty**: Variable — depends on when the right buyer appears

## Typical Timeline by Site Type

| Site Type | Marketing to Contract | Contract to Settlement | Total |
|---|---|---|---|
| Small residential site (1-4 dwellings) | 4-8 weeks | 6-10 weeks | 3-5 months |
| Medium apartment site (DA required) | 4-6 weeks | 6-12 months | 8-15 months |
| Large amalgamated site | 6-10 weeks | 9-18 months | 12-20 months |
| Site with existing DA | 3-6 weeks | 4-8 weeks | 2-4 months |

## How to Speed Up the Process

1. **Price realistically** from day one — the market knows what a site is worth
2. **Prepare thoroughly** — survey, planning assessment, and information memorandum ready before marketing
3. **Choose an agent with an active developer database** — they can generate interest immediately
4. **Be flexible on terms** — accommodating reasonable DA conditions attracts more buyers
5. **Respond quickly** to buyer enquiries and information requests

## Frequently Asked Questions

**Q: Is it faster to sell off-market?**
Sometimes. If your agent has a buyer already looking for a site like yours, an off-market sale can be very fast. But without competitive tension, you may achieve a lower price than a marketed campaign.

**Q: What if my site doesn't sell within the expected timeframe?**
Review the pricing, marketing strategy, and agent performance. If the market isn't responding, the price is usually too high. A price adjustment or change in sale method may be needed.

**Q: Can I set a deadline for the conditional period?**
Yes, and you should. A sunset clause sets an absolute deadline for conditions to be met. Typical sunset periods are 12-18 months.

---

*ACRES manages the development site sales process to deliver optimal timeframes and outcomes. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'How long does it take to sell a development site?', a: 'From listing to unconditional contract is typically 2-4 months. Including conditional periods for DA approval, total timeline is usually 6-18 months from listing to settlement.' },
      { q: 'Is it faster to sell a development site off-market?', a: 'Sometimes. If the agent has a ready buyer, off-market can be very fast. But without competitive tension, you may achieve a lower price than a marketed campaign.' },
      { q: 'What is the fastest way to sell a development site?', a: 'Sites with existing DAs and realistic pricing sell fastest — often 2-4 months from listing to settlement. Good preparation and a specialist agent with an active buyer database also speed up the process.' },
    ],
  },
  {
    id: 'when-is-best-time-to-sell-development-site',
    category: 'Market Intelligence',
    title: 'When Is the Best Time to Sell a Development Site in Brisbane? 2026 Market Update',
    excerpt: 'Market timing vs readiness — how to identify the right moment to take your property to market.',
    date: '2026-04-10',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1761479135364-4240520b5793?w=800&q=85',
    icon: Calendar,
    metaDescription: 'When is the best time to sell a development site? Market timing, seasonal patterns, and readiness indicators for development site vendors in Australia.',
    content: `
## The Timing Debate

Property vendors always ask: "When is the best time to sell?" For development sites, the answer depends on two factors: **market conditions** and **your personal readiness**.

## Market Timing Indicators

### Positive Signs (Good Time to Sell)
- **Strong pre-sale results** for apartment and townhouse projects in your area
- **Low supply** of development sites on the market
- **Active developer demand** — multiple developers looking to replenish their pipeline
- **Favourable finance conditions** — banks willing to lend for development
- **Population growth** driving housing demand
- **Infrastructure announcements** near your property (new train station, road upgrade)

### Cautionary Signs (Consider Waiting)
- **Oversupply** of completed apartments in your area
- **Rising construction costs** squeezing developer margins
- **Tightening finance** conditions making it harder for developers to secure loans
- **Economic uncertainty** causing developers to pause acquisitions
- **Recent rezoning** — the market may need time to adjust to new zoning and reflect the higher value

## Seasonal Patterns

Development site sales show some seasonal variation:

| Period | Activity Level | Notes |
|---|---|---|
| Feb-May | High | Developers return from holidays, active acquisition phase |
| Jun-Aug | Moderate | Mid-year assessment, some caution around financial year end |
| Sep-Nov | High | Sprint to secure sites before year end |
| Dec-Jan | Low | Holiday period, limited activity |

The best marketing campaigns typically launch in **February-March** or **September-October** when developer attention is highest.

## Personal Readiness

Market timing matters, but personal readiness is equally important. Ask yourself:

1. **Do you have somewhere to go?** Settlement of a development site can be 6-18 months away, but you need a plan
2. **Are you financially prepared?** Understand the tax implications before committing
3. **Is your property ready?** Survey, title, and planning assessment completed
4. **Are you emotionally ready?** Selling a long-held family home — even for a premium — can be difficult

## The Best Advice

Don't try to perfectly time the market. If the fundamentals are sound (strong zoning, good location, active developer demand) and you're personally ready, the right time to sell is **when you're prepared to execute well**.

A well-prepared site marketed by a specialist agent will achieve a strong result in almost any market condition.

## Frequently Asked Questions

**Q: Should I wait for my property to be rezoned before selling?**
If a rezoning is imminent and likely to increase density, waiting can be worthwhile. But rezoning processes are unpredictable and can take years. If your current zoning already supports development, selling now avoids the uncertainty.

**Q: Does the time of year matter for development site sales?**
Somewhat. February-March and September-October tend to see the most active developer demand. Avoid launching campaigns in December-January when the market is quiet.

**Q: How do I know if the market is good for sellers right now?**
Ask a specialist development site agent. They can provide current data on developer demand, recent comparable sales, and supply levels in your area.

---

*ACRES provides current market intelligence for development site sellers. Call 07 3096 0542 for an up-to-date assessment.*
    `,
    faq: [
      { q: 'When is the best time to sell a development site?', a: 'When market fundamentals are sound and you are personally prepared. February-March and September-October tend to see the most active developer demand. Avoid December-January.' },
      { q: 'Should I wait for rezoning before selling?', a: 'If rezoning is imminent and will increase density, waiting can be worthwhile. But rezoning is unpredictable. If your current zoning supports development, selling now avoids uncertainty.' },
      { q: 'How do I know if the market is good for sellers?', a: 'Ask a specialist development site agent for current data on developer demand, comparable sales, and supply levels in your area.' },
    ],
  },
  {
    id: 'should-i-get-development-approval-before-selling',
    category: 'Seller Guide',
    title: 'Should I Get Development Approval (DA) Before Selling My Property?',
    excerpt: 'The pros and cons of obtaining a DA before selling your development site.',
    date: '2026-04-09',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1774780864430-206ea507535e?w=800&q=85',
    icon: FileCheck,
    metaDescription: 'Should you get a development approval (DA) before selling your property as a development site? Pros, cons, costs, and when it makes sense.',
    content: `
## The Big Question

One of the most common questions from development site vendors is: "Should I get a DA before selling?" The answer depends on your circumstances, budget, and risk appetite.

## Arguments For Getting a DA First

### Higher Sale Price
A site with an approved DA typically commands a **10-20% premium** over an unapproved site. The developer avoids:
- DA preparation costs ($50,000-$150,000+ for consultants)
- Approval risk (the possibility of refusal)
- Time delay (6-12 months for the approval process)

### Faster Sale
Sites with existing approvals sell faster because the buyer's risk is significantly reduced. Unconditional contracts become more common.

### Broader Buyer Pool
Some developers only purchase DA-approved sites. Having approval opens your property to this additional buyer segment.

### Price Certainty
An approved scheme gives clear parameters for feasibility analysis, leading to more confident and higher offers.

## Arguments Against Getting a DA First

### Significant Upfront Cost
Obtaining a DA requires engaging:
- **Architect**: $20,000-$50,000+ for DA drawings
- **Town planner**: $10,000-$20,000 for the application
- **Engineers**: $5,000-$15,000 for civil, structural, and stormwater
- **Other consultants**: Traffic, acoustic, landscape, arborist as required

Total cost: **$50,000-$150,000+** depending on the project complexity.

### Risk of Refusal or Conditions
If the DA is refused or approved with onerous conditions, you've spent significant money and potentially reduced the site's attractiveness.

### Developer Preference
Many experienced developers prefer to buy unapproved sites so they can design their own project optimised for their target market, construction methodology, and financial model.

### Your Design May Not Match the Market
A DA designed to your architect's vision may not align with what the eventual developer wants to build. They may need to lodge a new DA anyway.

## When It Makes Sense

**Get a DA when:**
- The zoning is straightforward and approval is low risk
- You have the financial resources to invest upfront
- The site has complexity that a DA can resolve (e.g., demonstrating access or addressing flood)
- You want to sell unconditionally and quickly

**Skip the DA when:**
- The zoning is complex or contested
- You don't have $50,000-$150,000 to invest
- The site is clearly developable and developers can assess the potential easily
- Multiple developers are likely to want different designs

## The Middle Ground: Concept Design

A cost-effective compromise is to commission a **concept design** without lodging a DA:

- Cost: $5,000-$10,000 (much less than a full DA)
- Shows the development potential visually
- Demonstrates yield and building form
- Doesn't lock the developer into a specific design
- Helps buyers run feasibility quickly

This approach often achieves 80% of the benefit of a DA at 10% of the cost.

## Frequently Asked Questions

**Q: How much does it cost to get a DA?**
A full DA for a medium-density residential project typically costs $50,000-$150,000+ in consultant fees, council application fees, and related charges.

**Q: How long does it take to get a DA in Brisbane?**
Code-assessable applications typically take 2-4 months. Impact-assessable applications can take 6-12 months or longer.

**Q: What happens if the DA is refused?**
You lose the investment in the DA process. The property can still be sold as an unapproved site, but the refusal may need to be disclosed and could affect buyer confidence.

---

*ACRES can advise on whether obtaining a DA is worthwhile for your specific property. Call 07 3096 0542 for a free assessment.*
    `,
    faq: [
      { q: 'Should I get a DA before selling my development site?', a: 'It depends on your budget and risk appetite. A DA can increase the sale price by 10-20%, but costs $50,000-$150,000+. A concept design ($5,000-$10,000) is often a better cost-to-benefit compromise.' },
      { q: 'How much does a development approval cost?', a: 'A full DA for a medium-density residential project typically costs $50,000-$150,000+ in consultant fees, application fees, and related charges.' },
      { q: 'Do developers prefer to buy sites with or without DA?', a: 'It varies. Some developers only buy DA-approved sites. Others prefer unapproved sites so they can design their own project. A specialist agent can advise on what will attract the strongest demand for your site.' },
    ],
  },
  {
    id: 'private-treaty-vs-eoi-vs-auction-development-sites',
    category: 'Seller Guide',
    title: 'Private Treaty vs EOI vs Auction: Which Sale Method Is Best for Development Sites?',
    excerpt: 'Comparing sale methods and which delivers the best outcome for development site vendors.',
    date: '2026-04-08',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800&q=85',
    icon: Search,
    metaDescription: 'Comparing sale methods for development sites: private treaty, expressions of interest (EOI), and auction. Which method delivers the best price for development site vendors?',
    content: `
## Choosing the Right Sale Method

The sale method you choose significantly impacts the price you achieve and the type of buyer you attract. For development sites, the dynamics are quite different from residential house sales.

## Expressions of Interest (EOI)

### How It Works
Interested parties submit written offers by a specified deadline. There's no published price guide. The vendor assesses all offers and selects the best overall proposal — they're not obligated to accept any offer.

### Why EOI is Usually Best for Development Sites

**Advantages:**
- Creates a competitive deadline that drives urgency
- Allows flexible terms — buyers can tailor their conditions
- No price ceiling — buyers bid based on their own feasibility, which often exceeds vendor expectations
- Attracts serious, qualified buyers who can meet the deadline
- Your agent can negotiate with multiple parties simultaneously

**Disadvantages:**
- Requires a strong agent with an active developer database
- No guarantee of offers
- Less transparent than auction

### When to Use EOI
- Medium to large development sites
- Sites with strong development potential and multiple likely buyers
- When you want maximum flexibility in assessing offers

## Private Treaty

### How It Works
The property is listed at a specific price (or price range). Interested buyers negotiate directly with the vendor through the agent.

### When Private Treaty Works

**Advantages:**
- Clear price signal helps qualify genuine buyers
- Simpler process for straightforward sites
- No campaign deadline — flexible timing
- Good for smaller sites with a limited buyer pool

**Disadvantages:**
- Published price can cap offers (buyers rarely offer above the listed price)
- Less competitive tension than EOI
- Can take longer if the price isn't right

### When to Use Private Treaty
- Smaller sites suitable for townhouse or duplex development
- Sites in areas with limited developer demand
- When you have a clear idea of value and want a straightforward sale

## Auction

### How It Works
The property is sold publicly to the highest bidder on auction day. A reserve price protects the vendor from selling below an acceptable price.

### Why Auction is Rarely Best for Development Sites

**Advantages:**
- Complete transparency
- Creates urgency with a fixed deadline
- Unconditional sale (no conditions for the winning bidder)

**Disadvantages:**
- Developers typically need conditions (DA, finance) that auctions don't accommodate
- Unconditional requirement excludes many developer buyers
- Published reserve or price guide can cap bids
- Public process may not suit all vendors
- Higher marketing costs

### When to Use Auction
- Sites with broad appeal to both developers and owner-occupiers
- When unconditional sale is essential
- In very strong markets with high developer confidence

## Comparison Summary

| Factor | EOI | Private Treaty | Auction |
|---|---|---|---|
| **Best price** | Highest potential | Moderate | Variable |
| **Competitive tension** | High | Low-Moderate | High |
| **Conditional offers** | Yes | Yes | No |
| **Flexibility** | High | High | Low |
| **Timeline** | 4-6 weeks campaign | Variable | 4 weeks campaign |
| **Vendor cost** | Lower marketing | Lower marketing | Higher marketing |
| **Developer suitability** | Excellent | Good | Poor |

## Our Recommendation

For the majority of development sites, an **EOI campaign** delivers the best balance of competitive tension, price, and flexibility. It attracts serious developer buyers, creates urgency, and allows you to assess the complete picture — price, conditions, timeline, and buyer quality.

## Frequently Asked Questions

**Q: Can I switch sale methods if the first one doesn't work?**
Yes. If an EOI campaign doesn't generate sufficient interest, you can transition to private treaty or adjust pricing. Your agent should recommend a pivot strategy if needed.

**Q: What if I only receive one offer through EOI?**
One strong offer can still be negotiated to achieve a good outcome. Your agent may extend the campaign or approach additional buyers before closing.

**Q: Do I have to accept the highest offer in an EOI?**
No. EOI is not an auction. You can choose the offer that represents the best overall outcome considering price, conditions, buyer quality, and settlement terms.

---

*ACRES tailors the sale method to each development site for optimal results. Call 07 3096 0542 to discuss the best approach for your property.*
    `,
    faq: [
      { q: 'What is the best sale method for a development site?', a: 'Expressions of Interest (EOI) is usually best for development sites. It creates competitive tension, allows conditional offers, and gives flexibility to assess the complete picture of each offer.' },
      { q: 'Why is auction not ideal for development sites?', a: 'Auctions require unconditional bids, which excludes many developer buyers who need DA and finance conditions. This reduces the buyer pool and can result in a lower price.' },
      { q: 'Do I have to accept the highest offer in an EOI?', a: 'No. EOI is not an auction. You can choose the offer with the best overall outcome considering price, conditions, buyer quality, and settlement terms.' },
    ],
  },
  {
    id: 'how-infrastructure-projects-increase-land-value',
    category: 'Market Intelligence',
    title: 'How Infrastructure Projects Increase Your Land Value in Brisbane',
    excerpt: 'Understanding the value uplift from Cross River Rail, Brisbane Metro, and the 2032 Olympics.',
    date: '2026-04-07',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1757157295150-d9a04b8bb9cf?w=800&q=85',
    icon: Landmark,
    metaDescription: 'How infrastructure projects increase property and land values. Impact of Cross River Rail, Brisbane Metro, and 2032 Olympics on development site values in Brisbane.',
    keyFacts: [
      'Cross River Rail will create 6 new underground stations, with Woolloongabba station expected to transform the suburb',
      'Properties within 800m of new train stations historically see 10-25% value uplift within 5 years',
      'Brisbane Metro (electric bus rapid transit) will improve connectivity for inner-ring development corridors',
      'The 2032 Olympics is driving $8.1 billion in infrastructure across Brisbane, Gold Coast, and Sunshine Coast',
      'Infrastructure-driven rezoning can increase land values by 50-300% in affected suburbs',
    ],
    content: `
## The Infrastructure Effect

Major infrastructure projects don't just improve transport — they fundamentally reshape property values. For landowners near new stations, roads, or Olympic precincts, the value uplift can be transformational.

## How Infrastructure Creates Value

Infrastructure increases land value through three mechanisms:

### 1. Improved Accessibility
Better transport connections make your location more desirable. Properties within walking distance of new train stations see the greatest uplift because:
- Residents have easier access to employment centres
- Developers can achieve higher sale prices for completed product
- Councils often rezone surrounding land to higher density

### 2. Catalytic Rezoning
Governments frequently rezone land around new infrastructure to higher density. This is deliberate policy — infrastructure investment should be matched by housing supply near the investment.

Cross River Rail stations, for example, are expected to trigger significant rezoning in surrounding precincts, potentially allowing much higher development yields.

### 3. Precinct Transformation
Major infrastructure creates whole new precincts. Queens Wharf, Northshore Hamilton, and the Olympic village sites are transforming previously underutilised areas into vibrant mixed-use communities.

## Brisbane's Current Infrastructure Projects

### Cross River Rail
- **Impact zone**: Woolloongabba, Boggo Road, Albert Street, Roma Street
- **Value uplift**: 15-30% for properties within 800m of new stations
- **Timeline**: Now operational, opened 2026

### Brisbane Metro
- **Impact zone**: Cultural Centre, South Bank, UQ, key bus corridors
- **Value uplift**: 5-15% for properties near metro stops
- **Timeline**: Stage 1 operational, Stage 2 under development

### 2032 Olympics
- **Impact zone**: Woolloongabba (main stadium), Hamilton (athlete village), Redland Bay (whitewater), Chandler (aquatics)
- **Value uplift**: 10-25% in core Olympic precincts
- **Timeline**: Venue construction through 2030-2032

### Queens Wharf
- **Impact zone**: CBD, South Brisbane, West End
- **Value uplift**: CBD-adjacent properties benefiting from increased foot traffic and lifestyle amenity
- **Timeline**: Progressive opening 2026-2027

## Measuring the Value Impact

Research consistently shows property values increase near new transport infrastructure:

| Distance from Station | Typical Value Uplift |
|---|---|
| 0-400m | 15-30% |
| 400-800m | 10-20% |
| 800m-1.5km | 5-10% |
| 1.5km+ | Minimal direct impact |

These figures are for residential property generally. Development site values can see even greater uplift because zoning changes often accompany infrastructure, dramatically increasing developable yield.

## What This Means for Landowners

If your property is within the impact zone of a major infrastructure project:

1. **Check for planned rezoning** — council websites and state planning documents will indicate future zoning changes
2. **Understand the timeline** — selling just before or during construction often captures peak developer demand
3. **Get expert advice** — a specialist agent can quantify the infrastructure impact on your specific property
4. **Don't sell too early** — value uplift increases as infrastructure delivery becomes more certain

## Frequently Asked Questions

**Q: Will my rates increase if nearby infrastructure increases my land value?**
Possibly, but rate increases are generally modest compared to the value uplift. The increased property value is a net positive.

**Q: Should I sell before or after the infrastructure is completed?**
There's no single answer. Developer demand is often strongest during the construction phase when the outcome is certain but the infrastructure isn't yet operational. Consult a specialist agent for timing advice specific to your location.

**Q: How do I find out about planned infrastructure near my property?**
Check your local council's planning scheme, state government infrastructure plans (like the SEQ Regional Plan), and transport authority websites. Your property agent should also be able to advise.

---

*ACRES monitors infrastructure impacts on development site values across Brisbane. Call 07 3096 0542 to understand how current projects affect your property.*
    `,
    faq: [
      { q: 'How much does infrastructure increase property values?', a: 'Properties within 400m of new train stations typically see 15-30% value uplift. Development sites can see even greater increases when rezoning accompanies the infrastructure investment.' },
      { q: 'Should I sell before or after infrastructure is completed?', a: 'Developer demand is often strongest during construction when the outcome is certain but not yet operational. The optimal timing depends on your specific location and circumstances.' },
      { q: 'How do the 2032 Olympics affect Brisbane property values?', a: 'Core Olympic precincts (Woolloongabba, Hamilton) are expected to see 10-25% value uplift. The Olympics accelerate infrastructure delivery and urban renewal, creating sustained demand growth.' },
    ],
  },
  {
    id: 'why-your-corner-block-could-be-worth-more',
    category: 'Seller Guide',
    title: 'Why Your Corner Block Could Be Worth More Than You Think in 2026',
    excerpt: 'The hidden development value in corner properties that most homeowners overlook.',
    date: '2026-04-06',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1514897431180-fa36ff71d081?w=800&q=85',
    icon: MapPin,
    metaDescription: 'Why corner blocks are worth more as development sites. Dual frontage, density bonuses, and design advantages that make corner properties highly sought by developers.',
    keyFacts: [
      'Corner blocks typically command 10-25% price premiums over mid-block sites of equal size',
      'Dual street frontage allows for more flexible building designs with better natural light and ventilation',
      'Many council planning schemes provide density bonuses or reduced setback requirements for corner sites',
      'Corner blocks simplify vehicle access — developers can separate entry/exit points on different streets',
      'Even smaller corner blocks (500-600m2) can support duplex or tri-plex developments in MDR zones',
    ],
    content: `
## The Corner Block Advantage

If you own a corner block — a property with street frontage on two sides — you may be sitting on significantly more value than you realise. Corner blocks are among the most sought-after properties in the development site market.

## Why Developers Love Corner Blocks

### Dual Street Frontage
Two street frontages means:
- **Separate vehicle and pedestrian access** — cars enter from one street, people walk in from the other
- **Better apartment amenity** — more units get street-facing aspects and natural light
- **Improved building design** — architects have more flexibility to create attractive forms

### Higher Density Allowances
Some planning schemes allow additional height or density on corner sites. Brisbane's City Plan, for example, recognises that corner sites can accommodate more intensive development without the same neighbour impacts as mid-block sites.

### Superior Orientation
With two frontages, there's a much better chance that one side faces north — the premium orientation for residential development in Australia. North-facing apartments command higher sale prices.

### Visual Prominence
Corner buildings are seen from two streets, making them natural landmarks. Developers can create distinctive architecture that serves as a marketing advantage for the completed project.

## The Value Difference

In our experience at ACRES, corner blocks in medium density zones typically command a **20-40% premium** over comparable mid-block sites. The premium reflects:
- Higher potential yield (more dwellings achievable)
- Better end-product amenity (higher sale prices per dwelling)
- Reduced construction risk (better access, simpler logistics)

## What Makes Your Corner Block Even More Valuable

Your corner block has additional value if:
- It's zoned Medium Density or higher
- The lot size is 600m²+ (larger is better)
- One frontage faces north
- It's near public transport, shops, or employment
- The block is relatively flat
- There are no significant trees requiring retention
- Neighbouring properties could also be acquired (site consolidation potential)

## Case in Point

Consider a typical 700m² corner block in an inner Brisbane suburb zoned Medium Density:

- As a **family home**: Worth approximately $1,000,000
- As a **development site**: Worth $1,400,000 - $1,800,000 to a developer building 10-15 apartments

That's a potential premium of **$400,000 - $800,000** — simply because the block happens to be on a corner.

## Frequently Asked Questions

**Q: Is every corner block a development site?**
No. The block still needs appropriate zoning, minimum size, and market demand for the end product. But corners have a natural advantage over mid-block sites in the same zone.

**Q: Should I tell my neighbours about the development value?**
If your neighbour's property could be combined with yours to create a larger site, approaching them about a joint sale could significantly increase the total value. A specialist agent can facilitate this conversation.

**Q: Does a corner block attract more developer interest?**
Absolutely. When we market a corner block, the enquiry level and number of offers is consistently higher than for comparable mid-block sites.

---

*Own a corner block? Contact ACRES on 07 3096 0542 for a complimentary development site assessment.*
    `,
    faq: [
      { q: 'Why are corner blocks more valuable as development sites?', a: 'Corner blocks offer dual street frontage, allowing separate vehicle and pedestrian access, better apartment layouts, and often higher density allowances. They typically command 20-40% premiums over mid-block sites.' },
      { q: 'Is every corner block a development site?', a: 'No. The block still needs appropriate zoning, minimum size, and market demand. But corners have a natural advantage over mid-block sites in the same zone due to design flexibility and access benefits.' },
      { q: 'How much more is a corner block worth as a development site?', a: 'In inner Brisbane medium density zones, corner blocks can be worth 20-40% more than comparable mid-block sites — potentially $400,000-$800,000 more depending on size and location.' },
    ],
  },
  {
    id: 'brisbane-suburbs-where-developers-are-actively-buying',
    category: 'Market Intelligence',
    title: 'Brisbane Suburbs Where Developers Are Actively Buying in 2026',
    excerpt: 'Current hotspots for development site acquisitions across South East Queensland.',
    date: '2026-04-05',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1760120351693-e8418f79952f?w=800&q=85',
    icon: Building2,
    metaDescription: 'Brisbane suburbs where property developers are actively buying development sites. Current hotspots, price ranges, and what is driving demand in each area.',
    content: `
## Where the Action Is

Developer appetite for development sites varies significantly across Brisbane. Some suburbs see intense competition for every available site, while others have quietened. Here's where the active demand is right now.

## Inner South

### Woolloongabba
The epicentre of Brisbane's transformation. Cross River Rail and Olympic investment have created unprecedented developer interest. Sites within 800m of the new station are commanding premium prices.
- **Demand level**: Extremely high
- **Primary product**: Medium-to-high density apartments
- **Price range**: $3,000 - $5,000+ per m² of site area

### Coorparoo / Greenslopes
Strong demand driven by lifestyle appeal and connectivity. The established café and retail scene attracts quality developers targeting owner-occupiers.
- **Demand level**: High
- **Primary product**: Boutique apartments, townhouses
- **Price range**: $2,000 - $3,500 per m² of site area

## Inner West

### Toowong
University precinct demand combined with river frontage and excellent transport. Build-to-rent developers are increasingly active alongside traditional apartment developers.
- **Demand level**: High
- **Primary product**: Student accommodation, apartments, build-to-rent
- **Price range**: $2,500 - $4,000 per m² of site area

### Indooroopilly / Taringa
Benefiting from Toowong's ripple effect. Larger sites for townhouse and low-rise apartment development are well-positioned.
- **Demand level**: Moderate-High
- **Primary product**: Townhouses, low-rise apartments
- **Price range**: $1,800 - $3,000 per m² of site area

## Inner North

### Kelvin Grove
QUT precinct continues to drive demand for student and young professional housing. Limited site availability keeps prices firm.
- **Demand level**: High
- **Primary product**: Apartments, mixed-use
- **Price range**: $2,500 - $3,500 per m² of site area

### Clayfield / Albion
Emerging corridor with Albion Exchange transit-oriented development creating new interest. Established character and airport proximity support premium pricing.
- **Demand level**: Moderate-High
- **Primary product**: Townhouses, low-rise apartments
- **Price range**: $2,000 - $3,000 per m² of site area

## Inner City Fringe

### West End
Perennial favourite with strong lifestyle credentials. Limited remaining sites create intense competition when opportunities arise.
- **Demand level**: Very High
- **Primary product**: Boutique apartments, mixed-use
- **Price range**: $3,000 - $5,000 per m² of site area

### Newstead / Teneriffe
Premium waterfront market with sustained demand for high-end apartment product. Large sites are rare and command significant prices.
- **Demand level**: High
- **Primary product**: Premium apartments
- **Price range**: $3,500 - $6,000+ per m² of site area

## Growth Corridors

### Chermside / Kedron
Northern growth corridor with hospital precinct driving demand. Good value compared to inner-city suburbs.
- **Demand level**: Moderate
- **Primary product**: Townhouses, apartments
- **Price range**: $1,500 - $2,500 per m² of site area

### Moorooka / Rocklea
Southern corridor seeing increased interest as inner-city prices push developers further out. Good transport connectivity supports development.
- **Demand level**: Emerging
- **Primary product**: Townhouses, small apartments
- **Price range**: $1,200 - $2,000 per m² of site area

## What's Driving Demand

Across all suburbs, developers are most active where:
1. **Zoning supports the product they want to build** — clear, unambiguous density provisions
2. **End-buyer demand is proven** — strong sales evidence for comparable completed projects
3. **Transport connectivity is strong** — within walking distance of rail, bus, or metro
4. **Supply is constrained** — limited competing sites and projects in the pipeline

## Frequently Asked Questions

**Q: My suburb isn't listed here. Does that mean there's no development demand?**
Not necessarily. Demand exists in most Brisbane suburbs with appropriate zoning. This list highlights the most active markets right now, but opportunities exist across the city.

**Q: How do I find out the development demand in my specific area?**
Ask a specialist development site agent. At ACRES, we track developer enquiry and transaction activity across all Brisbane suburbs and can provide current intelligence for your location.

**Q: Are these price ranges per square metre of site or of building?**
These are per square metre of **site area** — the total area of your land. The price a developer pays per m² of site depends on the zoning, yield, and location.

---

*ACRES maintains real-time intelligence on developer demand across Brisbane. Call 07 3096 0542 to find out what your property is worth to the developer market.*
    `,
    faq: [
      { q: 'Where are developers actively buying development sites in Brisbane?', a: 'The most active markets are Woolloongabba, West End, Toowong, Kelvin Grove, and Newstead. Inner south and inner west suburbs are seeing the strongest demand, driven by infrastructure investment and population growth.' },
      { q: 'How much per square metre do developers pay for sites in Brisbane?', a: 'Prices range from $1,200/m² in emerging suburbs to $6,000+/m² in premium inner-city locations like Newstead and West End. The rate depends on zoning, yield potential, and location.' },
      { q: 'Is there development demand in outer Brisbane suburbs?', a: 'Yes, particularly in growth corridors with good transport connectivity. Chermside, Kedron, and the southern corridor are seeing increasing developer interest as inner-city prices rise.' },
    ],
  },
  {
    id: 'how-2032-olympics-will-affect-brisbane-property-values',
    category: 'Market Intelligence',
    title: 'How the 2032 Olympics Will Affect Property Values in Brisbane',
    excerpt: 'A deep dive into the precincts, projects, and property impacts of Brisbane\'s Olympic moment.',
    date: '2026-04-04',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1757157295150-d9a04b8bb9cf?w=800&q=85',
    icon: Medal,
    metaDescription: 'How will the 2032 Brisbane Olympics affect property values? Analysis of Olympic precincts, infrastructure, venue locations, and expected property value impacts.',
    keyFacts: [
      'Brisbane 2032 Olympics will inject an estimated $8.1 billion into Queensland infrastructure',
      'Key venue suburbs: Woolloongabba (main stadium), Hamilton (athletes village), Redland Bay (whitewater)',
      'Historical data: Sydney 2000 suburbs saw 20-40% value increases in the decade post-announcement',
      'Cross River Rail and Brisbane Metro are Olympic-linked projects already driving development demand',
      'Suburbs within 5km of Olympic venues are expected to see the strongest long-term capital growth',
    ],
    content: `
## Brisbane's Olympic Opportunity

The 2032 Brisbane Olympics will be the most significant event in the city's history. Beyond the sporting spectacle, the Olympics are accelerating billions of dollars in infrastructure delivery and urban renewal that will permanently reshape Brisbane's property landscape.

## Key Olympic Precincts

### Woolloongabba — The Main Stadium Precinct
The redeveloped Gabba will be the centrepiece of the 2032 Games. The surrounding precinct is being transformed from a predominantly low-rise suburban area into a high-density, mixed-use urban centre.

**Property impact:**
- Significant rezoning to higher density already underway
- Cross River Rail underground station opening nearby
- New public spaces, retail, and commercial development
- **Expected value uplift: 20-35%** for properties within 1km

### Hamilton — Athletes' Village
The Northshore Hamilton precinct will host the Athletes' Village, which converts to permanent housing after the Games.

**Property impact:**
- Major new residential precinct with thousands of dwellings
- Improved transport links and public amenity
- Waterfront parklands and recreational facilities
- **Expected value uplift: 15-25%** for surrounding properties

### Chandler — Aquatics and Indoor Sports
The Sleeman Sports Complex at Chandler will be upgraded for Olympic aquatics and indoor sports.

**Property impact:**
- Targeted infrastructure improvements in the eastern suburbs
- Improved connectivity to CBD via public transport
- Enhanced community facilities with legacy use
- **Expected value uplift: 10-15%** for nearby properties

## The Infrastructure Legacy

The Olympics are not just about venues. The real legacy is the infrastructure that supports the Games:

- **Transport upgrades**: New and improved rail, bus, and road connections
- **Urban renewal**: Transformation of underutilised precincts into vibrant communities
- **Public amenity**: New parks, plazas, waterfront promenades, and community facilities
- **Digital infrastructure**: 5G and smart city technology investments

This infrastructure creates permanent value that extends well beyond the 2032 event.

## Lessons from Past Olympic Cities

| City | Year | Property Impact |
|---|---|---|
| **Sydney** | 2000 | 10-15% uplift in Olympic precincts, sustained growth for 5+ years post-Games |
| **London** | 2012 | 50%+ uplift in Stratford/East London, complete neighbourhood transformation |
| **Barcelona** | 1992 | Waterfront transformation, 150% growth in Olympic village area over 20 years |

Every host city has seen significant property value uplift in Olympic precincts, with the benefits most pronounced in areas that received the greatest infrastructure investment.

## What This Means for Property Owners

### If You Own in an Olympic Precinct
Your property may be worth significantly more as a development site than it was before the Olympic announcement. Developers are actively seeking sites in these areas to build product that will be completed and sold around the Games period.

### Timing Considerations
- **2026-2029**: Peak development site acquisition period as developers secure pipeline
- **2028-2031**: Construction phase, some value uplift already captured
- **2032+**: Legacy benefits continue to support values post-Games

The optimal time to sell a development site in an Olympic precinct is typically **3-5 years before the event** — when developer demand is highest but not all value uplift has been captured in pricing.

### Don't Wait Too Long
Post-Olympics, the initial development rush subsides. While legacy values remain, the peak of developer demand and competition occurs in the lead-up to the Games.

## Frequently Asked Questions

**Q: Will my property be compulsorily acquired for the Olympics?**
Compulsory acquisition is rare and typically limited to land needed directly for venues or infrastructure. Most property owners in Olympic precincts benefit from value uplift without any acquisition risk.

**Q: How certain are the Olympic venue plans?**
The Brisbane 2032 Master Plan has been endorsed by the IOC, but specific designs and timelines may evolve. The key infrastructure commitments (transport, venues, village) are confirmed.

**Q: Should I sell my development site now or wait closer to 2032?**
Developer demand is typically strongest 3-5 years before the Games. Selling in the 2026-2029 window captures the acquisition phase when developers are building their pipeline.

---

*ACRES is actively working with landowners in Olympic precincts. Call 07 3096 0542 to understand how the 2032 Games affect your property's value.*
    `,
    faq: [
      { q: 'How will the 2032 Olympics affect Brisbane property values?', a: 'Olympic precincts like Woolloongabba, Hamilton, and Chandler are expected to see 15-35% value uplift. The Olympics accelerate infrastructure delivery and urban renewal that permanently reshapes property values.' },
      { q: 'When is the best time to sell near an Olympic precinct?', a: 'Developer demand is typically strongest 3-5 years before the Games. Selling in the 2026-2029 window captures peak acquisition demand when developers are building their project pipeline.' },
      { q: 'Will my property be acquired for the Olympics?', a: 'Compulsory acquisition is rare and limited to land directly needed for venues or infrastructure. Most property owners in Olympic precincts benefit from value uplift without acquisition risk.' },
    ],
  },
  {
    id: 'understanding-brisbane-city-plan-2014-landowners-guide',
    category: 'Guide',
    title: 'Understanding Brisbane City Plan 2014: A Landowner\'s Guide',
    excerpt: 'How to read the planning scheme that determines what can be built on your property.',
    date: '2026-04-03',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1761501668042-ab0f517aaf5a?w=800&q=85',
    icon: BookOpen,
    metaDescription: 'Understanding Brisbane City Plan 2014 for landowners. How to read the planning scheme, check zoning, understand overlays, and determine development potential.',
    content: `
## What is Brisbane City Plan 2014?

Brisbane City Plan 2014 is the planning scheme that governs all development within the Brisbane City Council area. It sets the rules for what can be built, where, and how. For landowners, understanding the City Plan is essential to knowing your property's development potential.

## The Key Components

### 1. Zones
Every property in Brisbane is assigned a zone that defines permissible land uses and development intensity. The main residential zones (from least to most intensive):

- **Low Density Residential**: Houses, granny flats
- **Character Residential**: Houses with pre-1947 character protections
- **Low-Medium Density Residential**: Townhouses, duplexes, triplexes
- **Medium Density Residential**: Walk-up apartments (3-5 storeys)
- **High Density Residential**: Apartment towers (6+ storeys)

### 2. Neighbourhood Plans
These are local area plans that apply specific provisions to defined suburbs or precincts. A neighbourhood plan may:
- Increase or restrict building heights beyond the base zone
- Require specific building setbacks or design treatments
- Identify sites for specific uses (e.g., mixed-use centres)
- Protect local character or views

Always check if your property falls within a neighbourhood plan — it may significantly affect what can be built.

### 3. Overlays
Additional planning layers that address site-specific issues:
- **Flood overlay**: Defines flood-prone land and building requirements
- **Heritage overlay**: Protects places of historical significance
- **Character overlay**: Protects the character of traditional housing areas
- **Infrastructure overlay**: Identifies land needed for future infrastructure
- **Airport environs overlay**: Manages development near airports
- **Bushfire overlay**: Manages development in bushfire-prone areas

### 4. Codes
Detailed technical requirements for specific types of development. Key codes for residential development include:
- **Dwelling house code**: Rules for individual houses
- **Multi-unit residential code**: Rules for apartments and townhouses
- **Centre design code**: Rules for development in centre zones

## How to Check Your Property

### Step by Step

1. Visit **cityplan.brisbane.qld.gov.au**
2. Click **"Interactive Mapping"**
3. Search your address
4. Toggle on the **Zoning** layer — this shows your base zone
5. Toggle on **Overlays** — check for flood, heritage, character, etc.
6. Toggle on **Neighbourhood Plan** — check if local provisions apply
7. Review the relevant **zone code** for building heights, setbacks, and density

### What to Look For

- **Maximum building height** (in metres and/or storeys)
- **Maximum plot ratio** (total floor area as a ratio of site area)
- **Maximum site coverage** (% of site that can be built on)
- **Minimum lot size** (for subdivision)
- **Setback requirements** (distances from boundaries)

## Recent and Upcoming Changes

Brisbane City Plan is regularly amended. Recent and upcoming changes relevant to development include:

- **Transit-oriented development provisions**: Higher density around key transport nodes
- **Housing supply amendments**: Relaxing some controls to increase housing delivery
- **Olympic precinct planning**: Special provisions for Games-related precincts

Stay informed about amendments that affect your property — they can significantly change development potential and value.

## Frequently Asked Questions

**Q: Where can I find Brisbane City Plan 2014?**
The full planning scheme and interactive mapping are available at cityplan.brisbane.qld.gov.au. The mapping tool allows you to search any address and view zoning, overlays, and neighbourhood plans.

**Q: How often does the planning scheme change?**
Brisbane City Plan is amended periodically through major and minor amendments. Major amendments can change zoning across entire suburbs. Minor amendments address specific issues. Changes are publicly notified.

**Q: Do I need a town planner to interpret the City Plan?**
For simple zoning checks, the interactive mapping tool is sufficient. For detailed development potential assessment, a town planner or specialist property advisor can interpret how the various provisions interact for your specific site.

---

*Not sure what your planning scheme means for your property? ACRES can interpret your development potential. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'Where can I find Brisbane City Plan 2014?', a: 'The full planning scheme and interactive mapping are available at cityplan.brisbane.qld.gov.au. Search your address to view zoning, overlays, and neighbourhood plans.' },
      { q: 'How do I check my property zoning in Brisbane?', a: 'Visit cityplan.brisbane.qld.gov.au, click Interactive Mapping, search your address, and toggle on the Zoning layer. Also check Overlays and Neighbourhood Plans for additional provisions.' },
      { q: 'How often does the Brisbane planning scheme change?', a: 'Brisbane City Plan is amended periodically. Major amendments can change zoning across entire suburbs. Changes are publicly notified and can significantly affect development potential and property values.' },
    ],
  },
  {
    id: 'what-is-site-consolidation-could-it-benefit-you',
    category: 'Seller Guide',
    title: 'What Is Site Consolidation? How Selling With Your Neighbour Gets a Higher Price',
    excerpt: 'How combining your property with a neighbour\'s can dramatically increase the total value.',
    date: '2026-04-02',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800&q=85',
    icon: Layers,
    metaDescription: 'What is site consolidation in property development? How combining properties with neighbours increases development value and what the process involves.',
    content: `
## What is Site Consolidation?

Site consolidation is the process of combining two or more adjacent properties into a single, larger development site. By amalgamating properties, the combined site often has significantly greater development potential than the individual properties would on their own.

## Why Bigger is Better

Many planning schemes set minimum site areas for different types of development. A single 600m² lot might only accommodate townhouses, but combined with a neighbour to create 1,200m², the site could support a full apartment building with substantially more dwellings.

**Example:**
- **600m² lot alone**: 4 townhouses (GRV $2.4M) → Land value ~$800,000
- **1,200m² consolidated site**: 15 apartments (GRV $9M) → Land value ~$2,400,000

The combined land value ($2,400,000) is **50% more** than the two individual values ($800,000 × 2 = $1,600,000). This premium — called the **amalgamation uplift** — is the incentive for neighbours to sell together.

## How the Process Works

### 1. Initial Assessment
A specialist agent or town planner assesses the combined development potential of your property and your neighbour's. This includes zoning analysis, yield estimation, and preliminary feasibility.

### 2. Neighbour Approach
This is the most sensitive step. The approach to your neighbour needs to be handled carefully — ideally through a trusted intermediary (your agent). The conversation should focus on the shared financial benefit.

### 3. Joint Agreement
If both parties are interested, you'll need to agree on:
- The split of the combined sale price (usually proportional to land area, but corner blocks may command a premium)
- Timeline for marketing and settlement
- Who bears the marketing costs
- A cooperation agreement documented by solicitors

### 4. Joint Marketing
The consolidated site is marketed as a single development opportunity. The larger site typically attracts more developer interest and higher per-square-metre pricing.

### 5. Contract Structure
The developer enters into separate contracts with each landowner, usually conditional on both contracts proceeding. This protects all parties.

## The Conversation with Your Neighbour

Approaching a neighbour about a joint sale can feel awkward. Tips for success:

- **Frame it as mutual benefit** — you're both better off selling together
- **Have the numbers ready** — show the estimated value difference
- **Use a professional intermediary** — your agent can make the initial approach
- **Be patient** — some neighbours need time to consider
- **Respect their decision** — if they're not interested, accept it gracefully

## When Consolidation Doesn't Work

- **Different owner timelines**: One owner needs to sell now, the other wants to wait 5 years
- **Unrealistic price expectations**: One owner overvalues their contribution to the combined site
- **Legal complications**: One property has a complex title or encumbrance
- **Neighbour refusal**: Not everyone wants to sell, and you can't force consolidation

## Frequently Asked Questions

**Q: How do we split the proceeds from a consolidated sale?**
Usually proportional to land area contribution. However, corner blocks or properties with particular advantages may negotiate a premium. Your agent and solicitor will help structure a fair split.

**Q: Can a developer consolidate the sites if I don't want to coordinate with my neighbour?**
Yes. Developers frequently approach adjacent landowners independently and manage the consolidation themselves. However, you may achieve a better outcome coordinating directly.

**Q: Does site consolidation always increase value?**
Almost always, when the combined site enables a step-change in development potential (e.g., from townhouses to apartments). The increase is most dramatic when the consolidated site crosses a minimum area threshold in the planning scheme.

---

*ACRES frequently manages site consolidation transactions across Brisbane. Call 07 3096 0542 if you think your property could benefit from a joint sale.*
    `,
    faq: [
      { q: 'What is site consolidation in property development?', a: 'Site consolidation combines two or more adjacent properties into a single larger development site. The combined site often has significantly greater development potential and value than individual properties.' },
      { q: 'How much more is a consolidated site worth?', a: 'The amalgamation uplift varies but can be 30-50% or more above the combined individual values, particularly when the larger site enables a step-change in development type (e.g., townhouses to apartments).' },
      { q: 'How do neighbours split the proceeds from a consolidated sale?', a: 'Usually proportional to land area contribution. Corner blocks or properties with particular advantages may negotiate a premium. Agents and solicitors help structure a fair split.' },
    ],
  },
  {
    id: 'downsizing-selling-to-developer-fund-retirement',
    category: 'Seller Guide',
    title: 'Downsizing in Brisbane? Why Selling to a Developer Could Fund Your Retirement',
    excerpt: 'How long-term homeowners in growth suburbs can unlock life-changing value from their property.',
    date: '2026-04-01',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1575275402664-9afd2de538ab?w=800&q=85',
    icon: Heart,
    metaDescription: 'Selling your home to a developer to fund retirement. How downsizers in Brisbane growth suburbs can unlock significantly more value by selling as a development site.',
    keyFacts: [
      'Retirees on large blocks in growth suburbs can receive 30-80% more by selling to a developer vs a home buyer',
      'Many developers offer extended settlement terms (12-24 months) so vendors can remain in their home while planning',
      'Downsizer contribution rules allow Australians 55+ to contribute up to $300,000 from home sale to super',
      'Popular downsizer-to-developer suburbs: Coorparoo, Greenslopes, Morningside, Holland Park, Camp Hill',
      'ACRES provides a free, no-obligation assessment to determine if your property has development potential',
    ],
    content: `
## The Downsizer Opportunity

If you've owned your family home for 20+ years in an inner Brisbane suburb, you may be sitting on a windfall. Suburbs that were quiet residential streets when you bought are now prime development corridors — and the land your home sits on could be worth far more than the house itself.

For retirees and downsizers, this represents a unique opportunity: sell your property as a development site, unlock a premium price, and fund the next chapter of your life.

## Why Your Property Might Be Worth More

### The Suburb Has Changed
Inner Brisbane suburbs have been progressively upzoned over the past two decades. Properties that were zoned for houses when you bought may now be zoned for townhouses or apartments. This rezoning can double or triple the land value.

### The Block Is Larger Than Modern Standards
Older homes typically sit on blocks of 600-1,000m² or more. Modern residential lots are often 300-450m². Your oversized block is exactly what developers need.

### Infrastructure Has Arrived
New train stations, bus corridors, and urban renewal projects have transformed your neighbourhood. Properties near these investments are the most sought-after development sites.

## How It Works in Practice

### The Numbers

Consider a typical scenario for a downsizer in an inner Brisbane suburb:

| | Residential Sale | Development Site Sale |
|---|---|---|
| Your property (800m², MDR zoning) | $1,100,000 | $1,600,000 - $1,800,000 |
| Less: Agent commission | $25,000 | $35,000 |
| Less: Legal fees | $2,000 | $3,000 |
| **Net proceeds** | **$1,073,000** | **$1,562,000 - $1,762,000** |
| **Additional value** | — | **$489,000 - $689,000** |

That additional $500,000-$700,000 could fund:
- A premium downsizer apartment or townhouse
- Travel and lifestyle for years
- Financial security and reduced reliance on superannuation
- A meaningful gift or inheritance for your family

### Tax Considerations

If the property has been your main residence for the entire ownership period, the capital gain is likely **CGT-free** under the main residence exemption. This makes the development premium even more valuable — it's largely tax-free money.

Additionally, the government's **downsizer contribution scheme** allows people aged 55+ to contribute up to $300,000 each ($600,000 per couple) from the sale of their home into superannuation, regardless of the usual contribution caps.

## The Process for Downsizers

1. **Get a development site assessment** — Find out what your property is worth to developers
2. **Decide on timing** — You don't need to have found your next home before listing
3. **Choose an agent** specialising in development sites — not a general residential agent
4. **Market the property** through an EOI or off-market campaign
5. **Negotiate settlement terms** that give you time to find and settle on your next home (typically 6-12 months)
6. **Move at your pace** — extended settlements mean you control the timeline

## Common Concerns

### "I don't want to leave my neighbourhood"
You don't have to. Many downsizers purchase an apartment or townhouse in the same suburb — or in a nearby completed development. Your agent can help identify suitable options.

### "The process seems complicated"
A specialist agent and solicitor handle the complexity. For you, the process is similar to any property sale — just with a higher price and more flexible terms.

### "My home has sentimental value"
This is completely understandable. But consider: the home will eventually be sold or passed on regardless. Selling as a development site ensures you capture the maximum value from a lifetime of ownership and investment.

### "Will the developer demolish my home?"
Most likely, yes. But the development will provide new homes for 10, 20, or 30+ families in a suburb you helped build into the desirable place it is today. There's a positive legacy in that.

## Frequently Asked Questions

**Q: Do I need to pay capital gains tax if I sell my home to a developer?**
If the property has been your primary residence for the entire ownership period, the capital gain is generally exempt from CGT under the main residence exemption. Consult a tax advisor for your specific situation.

**Q: Can I stay in my home during the sale process?**
Yes. You remain in the property through marketing, negotiation, and typically through the conditional period. Extended settlement terms (6-12 months) give you plenty of time to plan your next move.

**Q: How much more will I get selling to a developer vs a home buyer?**
It depends on zoning and location, but development premiums of 30-70% above residential value are common for well-located sites in medium-to-high density zones.

---

*If you're considering downsizing and want to understand your property's development potential, call ACRES on 07 3096 0542 for a confidential, no-obligation assessment.*
    `,
    faq: [
      { q: 'Can I sell my home to a developer to fund retirement?', a: 'Yes. If your property is in a growth suburb with medium-to-high density zoning, selling as a development site can unlock 30-70% more than a standard residential sale, often largely tax-free under the main residence exemption.' },
      { q: 'Do I pay CGT when selling my home as a development site?', a: 'If the property has been your primary residence for the entire ownership period, the capital gain is generally exempt from CGT. The government downsizer contribution scheme also allows additional super contributions.' },
      { q: 'How much more will I get from a developer than a home buyer?', a: 'Development premiums of 30-70% above residential value are common for well-located sites. On a $1.1M home, that could mean an additional $400,000-$700,000.' },
    ],
  },
];
